// ABOUTME: Root application component defining all routes and providers
// ABOUTME: Wraps app content in auth, timezone, and SignalR context providers
import React, { useState, useEffect, useMemo } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { shouldShowBlockingModal } from './utils/emailVerification';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { AdminJustificationProvider } from './contexts/AdminJustificationContext';
import { TimezoneProvider } from './contexts/TimezoneContext';
import { SignalRProvider } from './contexts/SignalRContext';
import { useAdminJustificationSetup } from './hooks/useAdminJustificationSetup';
import { useSubscriptionCheck } from './hooks/useSubscriptionCheck';
import { useImpersonation } from './hooks/useImpersonation';
import { useSiteSettings } from './hooks/useSiteSettings';
import { useEffectiveTheme } from './hooks/useEffectiveTheme';
import { initializeLemonSqueezy } from './utils/lemonsqueezy';
import { Box, CircularProgress, CssBaseline, GlobalStyles } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { resolveTheme } from './theme/themeRegistry';
import { getThemeCssVariables } from './theme/cssVariables';
import Login from './components/Login';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import VerifyEmail from './components/VerifyEmail';
import VerifyEmailChange from './pages/VerifyEmailChange';
import CancelEmailChange from './pages/CancelEmailChange';
import DataDeletionStatusPage from './pages/DataDeletionStatusPage';
import DashboardRedesigned from './components/DashboardRedesigned';
import ApiTest from './components/ApiTest';
import AuthCallback from './components/AuthCallback';
import CalendarOAuthCallback from './pages/CalendarOAuthCallback';
import FamilySetupWizard from './components/FamilySetupWizard';
import ChildDashboardBasic from './pages/ChildDashboardBasic';
import CrossingPage from './components/Crossing/CrossingPage';
import EventLog from './components/EventLog';
import GoalsPage from './components/GoalsPage';
import ChoresPage from './components/ChoresPage';
import NewAnalyticsPage from './components/NewAnalyticsPage';
import FamilyInsightsPage from './components/FamilyInsightsPage';
import AdminDashboard from './components/AdminDashboard';
import AdminUsageAnalytics from './components/AdminUsageAnalytics';
import AdminUserManagement from './components/AdminUserManagement';
import AdminUserDetail from './components/AdminUserDetail';
import AdminChildManagement from './components/AdminChildManagement';
import AdminThemePreview from './components/AdminThemePreview';
import AdminChildDetail from './components/AdminChildDetail';
import AdminBehaviorManagement from './components/AdminBehaviorManagement';
import AdminFamilyManagement from './components/AdminFamilyManagement';
import AdminFamilyDetail from './components/AdminFamilyDetail';
import AdminPolicyManagement from './components/AdminPolicyManagement';
import AdminSystemBackups from './components/AdminSystemBackups';
import AdminSettings from './components/AdminSettings';
import AdminAuditLog from './components/AdminAuditLog';
import AdminWebhookLog from './components/AdminWebhookLog';
import AdminEmailTemplatesPage from './components/AdminEmailTemplatesPage';
import AdminCampaigns from './components/AdminCampaigns';
import CampaignDetailPage from './pages/CampaignDetailPage';
import KnowledgeBaseTab from './components/admin/KnowledgeBaseTab';
import MyProfile from './components/MyProfile';
import MyFamilyPage from './components/MyFamilyPage';
import ChildProfilePage from './components/ChildProfilePage';
import IntegrationsPage from './components/IntegrationsPage';
import AboutPage from './components/AboutPage';
import KnowledgeBaseLibrary from './components/knowledgeBase/KnowledgeBaseLibrary';
import ArticleDetailPage from './components/knowledgeBase/ArticleDetailPage';
import VideoPlayer from './components/knowledgeBase/VideoPlayer';
import ErrorBoundary from './components/ErrorBoundary';
import PrivacyPolicyPage from './components/PrivacyPolicyPage';
import TermsOfServicePage from './components/TermsOfServicePage';
import EmailPreferencesPage from './components/EmailPreferencesPage';
import PolicyReviewPage from './components/PolicyReviewPage';
import PolicyAcceptanceModal from './components/PolicyAcceptanceModal';
import SubscriptionBlockingModal from './components/SubscriptionBlockingModal';
import { checkUserPolicyAcceptance, familyAPI } from './services/api';
import SharedAppBar from './components/SharedAppBar';
import InvitationAcceptance from './components/InvitationAcceptance';
import InvitedUserOnboarding from './components/InvitedUserOnboarding';
import PendingInvitationsPage from './components/PendingInvitationsPage';
import SubscriptionStatusRibbon from './components/SubscriptionStatusRibbon';
import ImpersonationBanner from './components/ImpersonationBanner';
import ImpersonationGuard from './components/ImpersonationGuard';
import EmailVerificationRibbon from './components/EmailVerificationRibbon';
import EmailVerificationModal from './components/EmailVerificationModal';
import TimezoneAwareRoute from './components/TimezoneAwareRoute';
// import ThemeShowcase from './components/ThemeShowcase';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  return user ? <>{children}</> : <Navigate to="/login" replace />;
}

export function PolicyEnforcedRoute({ children }: { children: React.ReactNode }) {
  const { user, logout } = useAuth();
  const { isImpersonating, isLoading: impersonationLoading } = useImpersonation();
  const [policyStatus, setPolicyStatus] = useState<{
    requiresAcceptance: boolean;
    lastAcceptedVersions: { privacyPolicy: number | null; termsOfService: number | null };
    currentVersions: { privacyPolicy: number; termsOfService: number };
  } | null>(null);
  const [checkingPolicy, setCheckingPolicy] = useState(true);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    // Wait for impersonation state to be confirmed before checking policies
    if (impersonationLoading) return;

    if (user && !isImpersonating) {
      checkUserPolicyAcceptance()
        .then(status => {
          setPolicyStatus(status);
          setShowModal(status.requiresAcceptance);
        })
        .catch(err => {
          console.error('Failed to check policy acceptance:', err);
          // On error, don't block the user
        })
        .finally(() => {
          setCheckingPolicy(false);
        });
    } else {
      // Not logged in, or admin is impersonating - skip policy gate
      // eslint-disable-next-line react-hooks/set-state-in-effect -- synchronous reset is intentional; effect handles both async (via .finally()) and sync (else branch) paths
      setCheckingPolicy(false);
    }
  }, [user, isImpersonating, impersonationLoading]);

  if (!user) return <Navigate to="/login" replace />;

  if (checkingPolicy) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      {children}
      {showModal && policyStatus && (
        <PolicyAcceptanceModal
          open={showModal}
          onAccepted={() => setShowModal(false)}
          onLogout={logout}
          userLastAcceptedVersions={policyStatus.lastAcceptedVersions}
          isExistingUser={true}
        />
      )}
    </>
  );
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (!user.isAdmin) return <Navigate to="/" replace />;
  return (
    <PolicyEnforcedRoute>
      {children}
    </PolicyEnforcedRoute>
  );
}

function EmailPreferencesPageWithNav() {
  const navigate = useNavigate();
  
  const handleBackClick = () => {
    navigate('/profile');
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <SharedAppBar 
        title="Email Preferences" 
        showBackButton={true}
        onBackClick={handleBackClick}
      />
      <EmailPreferencesPage />
    </Box>
  );
}

export function AppContent() {
  const { user, isLoading, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // Initialize with browser timezone so ChildCalendarPanel can start loading immediately.
  // When the API chain resolves, setFamilyTimezone updates to the server value and
  // fetchEvents re-runs with the correct family timezone.
  // Note: browsers in UTC environments return "UTC" here, which the backend treats as
  // invalid and falls back to browser timezone — still better than undefined (skeleton state).
  const [familyTimezone, setFamilyTimezone] = useState<string | undefined>(
    () => Intl.DateTimeFormat().resolvedOptions().timeZone
  );
  const effectiveThemeName = useEffectiveTheme();
  const { data: siteSettings } = useSiteSettings();
  useAdminJustificationSetup();
  const showVerificationModal = useMemo(() => {
    if (user && location.pathname !== '/verify-email') {
      const requireEmailVerification = siteSettings?.requireEmailVerification ?? true;
      return shouldShowBlockingModal(user, familyTimezone, requireEmailVerification);
    }
    return false;
  }, [user, location.pathname, familyTimezone, siteSettings]);

  // Subscription check hook
  const { shouldShowModal, lockoutReason } = useSubscriptionCheck();
  const { impersonatedUser } = useImpersonation();

  // Fetch family timezone when user logs in or impersonation changes.
  // Uses familyAPI (axios-based) so the X-Impersonate-User-Id header is included,
  // ensuring the impersonated family's timezone is fetched when impersonating.
  useEffect(() => {
    const fetchFamilyTimezone = async () => {
      if (user) {
        try {
          const families = await familyAPI.getFamilies();
          if (families.length > 0) {
            const timezone = await familyAPI.getFamilyTimezone(families[0].familyId);
            // Remove quotes if the API returns a quoted string
            setFamilyTimezone(timezone.replace(/^"|"$/g, ''));
          }
        } catch (error) {
          console.error('Failed to fetch family timezone:', error);
          // familyTimezone is already initialized to browser timezone; this just means the family
          // timezone won't be updated to the server value — browser timezone remains as fallback
        }
      }
    };

    fetchFamilyTimezone();
  }, [user, impersonatedUser?.userId]);

  const resolvedTheme = resolveTheme(effectiveThemeName);

  // Handle subscription management navigation
  const handleManageSubscription = () => {
    navigate('/family');
  };

  // Handle logout
  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  if (isLoading || effectiveThemeName === undefined) {
    return (
      <ThemeProvider theme={resolvedTheme}>
        <GlobalStyles styles={getThemeCssVariables(resolvedTheme)} />
        <CssBaseline />
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: theme => theme.palette.gradient.secondary,
          }}
        >
          <CircularProgress size={60} thickness={4} />
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={resolvedTheme}>
    <GlobalStyles styles={getThemeCssVariables(resolvedTheme)} />
    <CssBaseline />
    <SignalRProvider isAuthenticated={!!user}>
    <TimezoneProvider familyTimezone={familyTimezone}>
      {/* Subscription Blocking Modal - Issue #1027 */}
      {shouldShowModal && lockoutReason && (
        <SubscriptionBlockingModal
          open={shouldShowModal}
          lockoutReason={lockoutReason}
          onManageSubscription={handleManageSubscription}
          onLogout={handleLogout}
        />
      )}

      {/* Email Verification Modal - Issue #1048 */}
      <EmailVerificationModal
        open={showVerificationModal}
        onLogout={handleLogout}
      />

      {/* Impersonation Banner - Issue #1133 - Must be first for proper visibility */}
      <ImpersonationBanner />

      {/* Trial Countdown Ribbon - Issue #1026 */}
      <SubscriptionStatusRibbon currentPath={location.pathname} />

      {/* Email Verification Ribbon - Issue #1048, suppression Issue #1234 */}
      <EmailVerificationRibbon currentPath={location.pathname} />

      <ImpersonationGuard>
        <Routes>
        {/* Public routes */}
        <Route path="/login" element={<Login />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/verify-email-change" element={<VerifyEmailChange />} />
      <Route path="/cancel-email-change" element={<CancelEmailChange />} />
      <Route path="/data-deletion-status" element={<DataDeletionStatusPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/calendar/oauth/callback" element={<CalendarOAuthCallback />} />
      <Route path="/invitation/:token" element={<InvitationAcceptance />} />
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/terms" element={<TermsOfServicePage />} />
      <Route path="/policies/review" element={<PolicyReviewPage />} />
      
      {/* Child dashboard - URL slug access (generated by My Family page) */}
      <Route path="/child-dashboard/:urlSlug" element={<ChildDashboardBasic />} />

      {/* Crossing route host (Issue #1939) — reachable by JWT parent or slug+token child; not
          wrapped in ProtectedRoute since a child has no JWT. CrossingPage self-authorizes. */}
      <Route path="/crossing/:crossingId" element={
        <TimezoneAwareRoute>
          <CrossingPage />
        </TimezoneAwareRoute>
      } />
      
      {/* Child dashboard - anonymous access with token authentication (tablets) */}
      <Route path="/family/:familyId/:slug/child/:childName/:token" element={
        <TimezoneAwareRoute>
          <ChildDashboardBasic />
        </TimezoneAwareRoute>
      } />

      <Route path="/ohana/:familyId/:slug/child/:childName/:token" element={
        <TimezoneAwareRoute>
          <ChildDashboardBasic />
        </TimezoneAwareRoute>
      } />
      
      {/* Protected child dashboard */}
      <Route path="/child/:childId/dashboard" element={
        <ProtectedRoute>
          <ChildDashboardBasic />
        </ProtectedRoute>
      } />
      
      {/* Alternative route for child dashboard (simpler pattern) */}
      <Route path="/child/:childId" element={
        <ProtectedRoute>
          <ChildDashboardBasic />
        </ProtectedRoute>
      } />
      
      {/* Protected routes */}
      <Route path="/" element={
        <PolicyEnforcedRoute>
          <DashboardRedesigned />
        </PolicyEnforcedRoute>
      } />
      
      <Route path="/event-log" element={
        <ProtectedRoute>
          <EventLog />
        </ProtectedRoute>
      } />
      
      <Route path="/onboarding/invited" element={
        <ProtectedRoute>
          <InvitedUserOnboarding />
        </ProtectedRoute>
      } />

      <Route path="/invites" element={
        <ProtectedRoute>
          <PendingInvitationsPage />
        </ProtectedRoute>
      } />

      <Route path="/profile" element={
        <ProtectedRoute>
          <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
            <SharedAppBar title="My Profile" />
            <MyProfile />
          </Box>
        </ProtectedRoute>
      } />
      
      <Route path="/about" element={
        <ProtectedRoute>
          <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
            <SharedAppBar title="About" />
            <AboutPage />
          </Box>
        </ProtectedRoute>
      } />
      
      <Route path="/profile/email-preferences" element={
        <ProtectedRoute>
          <EmailPreferencesPageWithNav />
        </ProtectedRoute>
      } />
      
      <Route path="/goals" element={
        <ProtectedRoute>
          <GoalsPage />
        </ProtectedRoute>
      } />

      <Route path="/responsibilities" element={
        <ProtectedRoute>
          <ChoresPage />
        </ProtectedRoute>
      } />

      <Route path="/family/child/:childId" element={
        <ProtectedRoute>
          <ChildProfilePage />
        </ProtectedRoute>
      } />

      <Route path="/analytics" element={
        <ProtectedRoute>
          <NewAnalyticsPage />
        </ProtectedRoute>
      } />

      <Route path="/analytics-v2" element={
        <ProtectedRoute>
          <FamilyInsightsPage />
        </ProtectedRoute>
      } />
      
      <Route path="/family" element={
        <ProtectedRoute>
          <TimezoneAwareRoute>
            <MyFamilyPage />
          </TimezoneAwareRoute>
        </ProtectedRoute>
      } />

      <Route path="/ohana" element={
        <ProtectedRoute>
          <TimezoneAwareRoute>
            <MyFamilyPage />
          </TimezoneAwareRoute>
        </ProtectedRoute>
      } />
      
      <Route path="/integrations" element={
        <ProtectedRoute>
          <IntegrationsPage />
        </ProtectedRoute>
      } />

      {/* Knowledge Base routes - Public accessible, access control enforced by backend */}
      <Route path="/knowledge-base" element={
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
          <SharedAppBar title="Knowledge Base" />
          <ErrorBoundary>
            <KnowledgeBaseLibrary />
          </ErrorBoundary>
        </Box>
      } />

      <Route path="/knowledge-base/article/:id" element={
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
          <SharedAppBar title="Knowledge Base" />
          <ErrorBoundary>
            <ArticleDetailPage />
          </ErrorBoundary>
        </Box>
      } />

      <Route path="/knowledge-base/video/:id" element={
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
          <SharedAppBar title="Knowledge Base" />
          <ErrorBoundary>
            <VideoPlayer />
          </ErrorBoundary>
        </Box>
      } />

      <Route path="/setup" element={
        <ProtectedRoute>
          <FamilySetupWizard />
        </ProtectedRoute>
      } />
      
      <Route path="/family-setup" element={
        <ProtectedRoute>
          <TimezoneAwareRoute>
            <FamilySetupWizard />
          </TimezoneAwareRoute>
        </ProtectedRoute>
      } />

      <Route path="/ohana-setup" element={
        <ProtectedRoute>
          <TimezoneAwareRoute>
            <FamilySetupWizard />
          </TimezoneAwareRoute>
        </ProtectedRoute>
      } />
      
      {/* API test route with query param */}
      <Route path="/api-test" element={<ApiTest />} />
      
      {/* Admin routes */}
      <Route path="/admin" element={
        <AdminRoute>
          <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
            <SharedAppBar title="BePono Admin" position="sticky" />
            <AdminDashboard />
          </Box>
        </AdminRoute>
      } />
      
      <Route path="/admin/usage-analytics" element={
        <AdminRoute>
          <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
            <SharedAppBar title="Usage Analytics" position="sticky" showBackButton={true} onBackClick={() => window.location.href = '/admin'} />
            <AdminUsageAnalytics />
          </Box>
        </AdminRoute>
      } />
      
      <Route path="/admin/users" element={
        <AdminRoute>
          <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
            <SharedAppBar title="User Management" position="sticky" showBackButton={true} onBackClick={() => window.location.href = '/admin'} />
            <AdminUserManagement />
          </Box>
        </AdminRoute>
      } />
      
      <Route path="/admin/users/:userId" element={
        <AdminRoute>
          <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
            <SharedAppBar title="User Details" position="sticky" showBackButton={true} onBackClick={() => window.location.href = '/admin/users'} />
            <AdminUserDetail />
          </Box>
        </AdminRoute>
      } />
      
      <Route path="/admin/children" element={
        <AdminRoute>
          <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
            <SharedAppBar title="Child Management" position="sticky" showBackButton={true} onBackClick={() => window.location.href = '/admin'} />
            <AdminChildManagement />
          </Box>
        </AdminRoute>
      } />

      <Route path="/admin/theme-preview" element={
        <AdminRoute>
          <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
            <SharedAppBar title="Theme Preview" position="sticky" showBackButton={true} onBackClick={() => window.location.href = '/admin'} />
            <AdminThemePreview />
          </Box>
        </AdminRoute>
      } />

      <Route path="/admin/children/:childId" element={
        <AdminRoute>
          <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
            <SharedAppBar title="Child Details" position="sticky" showBackButton={true} onBackClick={() => window.location.href = '/admin/children'} />
            <AdminChildDetail />
          </Box>
        </AdminRoute>
      } />
      
      <Route path="/admin/behaviors" element={
        <AdminRoute>
          <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
            <SharedAppBar title="Behavior Management" position="sticky" showBackButton={true} onBackClick={() => window.location.href = '/admin'} />
            <AdminBehaviorManagement />
          </Box>
        </AdminRoute>
      } />
      
      <Route path="/admin/families" element={
        <AdminRoute>
          <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
            <SharedAppBar title="Family Management" position="sticky" showBackButton={true} onBackClick={() => window.location.href = '/admin'} />
            <AdminFamilyManagement />
          </Box>
        </AdminRoute>
      } />
      
      <Route path="/admin/families/:familyId" element={
        <AdminRoute>
          <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
            <SharedAppBar title="Family Details" position="sticky" showBackButton={true} onBackClick={() => window.location.href = '/admin/families'} />
            <AdminFamilyDetail />
          </Box>
        </AdminRoute>
      } />
      
      <Route path="/admin/policies" element={
        <AdminRoute>
          <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
            <SharedAppBar title="Policy Management" position="sticky" showBackButton={true} onBackClick={() => window.location.href = '/admin'} />
            <AdminPolicyManagement />
          </Box>
        </AdminRoute>
      } />
      
      <Route path="/admin/system/backups" element={
        <AdminRoute>
          <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
            <SharedAppBar title="System Backups" position="sticky" showBackButton={true} onBackClick={() => window.location.href = '/admin'} />
            <AdminSystemBackups />
          </Box>
        </AdminRoute>
      } />
      
      <Route path="/admin/audit" element={
        <AdminRoute>
          <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
            <SharedAppBar title="Audit Log" position="sticky" showBackButton={true} onBackClick={() => window.location.href = '/admin'} />
            <AdminAuditLog />
          </Box>
        </AdminRoute>
      } />

      <Route path="/admin/webhooks" element={
        <AdminRoute>
          <AdminWebhookLog />
        </AdminRoute>
      } />

      <Route path="/admin/settings" element={
        <AdminRoute>
          <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
            <SharedAppBar title="System Settings" position="sticky" showBackButton={true} onBackClick={() => window.location.href = '/admin'} />
            <AdminSettings />
          </Box>
        </AdminRoute>
      } />
      
      <Route path="/admin/email-templates" element={
        <AdminRoute>
          <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
            <SharedAppBar title="Email Templates" position="sticky" showBackButton={true} onBackClick={() => window.location.href = '/admin'} />
            <AdminEmailTemplatesPage />
          </Box>
        </AdminRoute>
      } />

      <Route path="/admin/campaigns" element={
        <AdminRoute>
          <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
            <SharedAppBar title="Campaign Management" position="sticky" showBackButton={true} onBackClick={() => window.location.href = '/admin'} />
            <AdminCampaigns />
          </Box>
        </AdminRoute>
      } />

      <Route path="/admin/campaigns/:id" element={
        <AdminRoute>
          <CampaignDetailPage />
        </AdminRoute>
      } />

      <Route path="/admin/knowledge-base" element={
        <AdminRoute>
          <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
            <SharedAppBar title="Knowledge Base Management" position="sticky" showBackButton={true} onBackClick={() => window.location.href = '/admin'} />
            <KnowledgeBaseTab />
          </Box>
        </AdminRoute>
      } />

        {/* Catch all - redirect to dashboard or login */}
        <Route path="*" element={
          user ? <Navigate to="/" replace /> : <Navigate to="/login" replace />
        } />
      </Routes>
      </ImpersonationGuard>
    </TimezoneProvider>
    </SignalRProvider>
    </ThemeProvider>
  );
}

class AppRootErrorBoundary extends React.Component<{children: React.ReactNode}, {hasError: boolean, error: Error | null}> {
  constructor(props: {children: React.ReactNode}) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Error caught by boundary:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <Box sx={{ padding: '20px' }}>
          <h1>Something went wrong.</h1>
          <pre>{this.state.error?.toString()}</pre>
          <button onClick={() => window.location.reload()}>Reload</button>
        </Box>
      );
    }

    return this.props.children;
  }
}

function App() {
  // Initialize LemonSqueezy SDK on app mount (Issue #1030)
  useEffect(() => {
    initializeLemonSqueezy();
  }, []);

  return (
    <AppRootErrorBoundary>
      <Router>
        <AuthProvider>
          <AdminJustificationProvider>
            <AppContent />
          </AdminJustificationProvider>
        </AuthProvider>
      </Router>
    </AppRootErrorBoundary>
  );
}

export default App;
